import React from "react";
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Star, Trash2 } from "lucide-react";

type Photo = { id: string; url: string; kind: string; caption?: string; room_id?: string | null };

function Thumb({ p, index, src, label, onDelete, onCover, onEdit }: { p: Photo; index: number; src: string; label: string; onDelete: () => void; onCover: () => void; onEdit: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: p.id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 20 : undefined };
  return (
    <div ref={setNodeRef} style={style} data-testid={`photo-thumb-${p.id}`}
      className={`relative aspect-square rounded-xl overflow-hidden bg-slate-100 ${index === 0 ? "ring-2 ring-amber-400" : ""} ${isDragging ? "shadow-xl opacity-90" : ""}`}>
      <img src={src} alt={label} className="h-full w-full object-cover pointer-events-none select-none" loading="lazy" draggable={false} />
      <button type="button" {...attributes} {...listeners} onClick={onEdit} data-testid={`photo-drag-${p.id}`} aria-label={`Edit or drag ${label}`}
        className="absolute inset-0 cursor-grab active:cursor-grabbing touch-none" />
      <span className="pointer-events-none absolute top-9 left-1 right-1 text-[10px] font-semibold text-white drop-shadow truncate">{label}</span>
      <span className="pointer-events-none absolute top-1 left-1 h-6 w-6 grid place-items-center rounded-full bg-white/85 text-slate-600"><GripVertical className="h-3.5 w-3.5" /></span>
      {index === 0 ? (
        <span data-testid={`photo-cover-badge-${p.id}`} className="pointer-events-none absolute bottom-1 left-1 text-[10px] font-bold bg-amber-400 text-amber-950 rounded px-1.5 py-0.5 flex items-center gap-0.5"><Star className="h-3 w-3" />Cover</span>
      ) : (
        <button type="button" data-testid={`photo-make-cover-${p.id}`} onClick={onCover} className="absolute bottom-1 left-1 text-[10px] font-semibold bg-black/60 text-white rounded px-1.5 py-1 flex items-center gap-0.5">
          <Star className="h-3 w-3" />Make cover
        </button>
      )}
      <button type="button" data-testid={`photo-delete-${p.id}`} onClick={onDelete} className="absolute top-1 right-1 h-8 w-8 grid place-items-center rounded-full bg-white/90 text-rose-600"><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}

export function SortablePhotos({ photos, srcOf, labelOf, onReorder, onDelete, onEdit }: {
  photos: Photo[]; srcOf: (p: Photo) => string; labelOf: (p: Photo) => string; onReorder: (ids: string[]) => void; onDelete: (id: string) => void; onEdit: (p: Photo) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = photos.map((p) => p.id);
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    onReorder(arrayMove(ids, ids.indexOf(String(active.id)), ids.indexOf(String(over.id))));
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-3 gap-2" data-testid="photos-sortable-grid">
          {photos.map((p, i) => (
            <Thumb key={p.id} p={p} index={i} src={srcOf(p)} label={labelOf(p)} onDelete={() => onDelete(p.id)} onEdit={() => onEdit(p)}
              onCover={() => onReorder([p.id, ...ids.filter((x) => x !== p.id)])} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
